/**
 * Explanations and helper sentences (Miles, 2026-10-23: none on screen; streamlined and simple).
 * Each is empty, so it's hidden. To bring one back, paste its old wording (in the note) in.
 */

// ---- PriceWatch
/** Was: "Costco refunds the difference if a price drops within 30 days of buying. Type a lower price you see (in the warehouse, the app or the coupon book) and the app works out what to ask for, tax included." */
export const PRICE_WATCH_NOTE = "";

// ---- ReceiptScan
/** Was: "Take a photo and the vision model in LM Studio reads the items, coupons and tax marks. You check everything before it's added. The photo goes only to your computer." */
export const RECEIPT_SCAN_NOTE = "";

// ---- ReceiptTests
/** Was: "Uses the model loaded in LM Studio (set on the Ask page). A good vision model gets 90%+ on these; the dim photo is the hard one." */
export const RECEIPT_TESTS_NOTE = "";

// ---- SplitControls
/** Was: "Weights like "Emily 2, Chloe 1", or dollars like "Emily $10" (everyone else splits the rest)." */
export const SPLIT_WEIGHTS_HINT = "";
/** Was: "Leave someone blank and they split whatever's left." */
export const SPLIT_BLANK_HINT = "";

// ---- Accounts
/** Was: "Not linked to anyone, so they can't see anything yet." */
export const ACCOUNT_UNLINKED_NOTE = "";
/** Was: "Deletes the account and its access. Their items and payments in your groups stay." */
export const ACCOUNT_DELETE_NOTE = "";

// ---- Ask
/** Was: "Your questions and ledger data go from this browser straight to your computer. Nothing passes through the Split Ledger server." */
export const ASK_PRIVACY_NOTE = "";
/** Was: "Fix any number before saving." */
export const ASK_FIX_NOTE = "";
/** Was: "Chats are kept on this device, separately for each group." */
export const ASK_CHATS_NOTE = "";

// ---- GroupSettings
/** Was: "Balances count what everyone else owes this person. It's also who you are in Member view." */
export const GS_YOU_NOTE = "";
/**
 * Shown under "Which one is you" when it's someone with their own member account ({name}): that
 * person is them, so their items and balance would count as yours. Empty hides it.
 */
export const GS_YOU_IS_SOMEONE_ELSE =
  "{name} has their own account, so they can't be you here. Pick the person who is you, or Not set.";
/** Next to a person in the "Which one is you" list who has their own member account. */
export const GS_YOU_HAS_ACCOUNT = " (own account)";
/** Was: "Everyone with an account sees the same Ledger: every item, and they tick only themselves. Choose which other pages each role gets in this group, then change pages and categories for one person if you need to. What's off is left out on the server, not just hidden. You always see everything; your cards and private item notes never show to anyone." */
export const GS_ACCESS_NOTE = "";
/** Was: "When someone makes an account, they pick the groups they're in. Saying they're someone already here always waits for you." */
export const GS_JOINING_NOTE = "";
/** Was: "The Tests group is only yours, so it never shows on sign-up." */
export const GS_TESTS_NOTE = "";
/** Was: "Archived people keep their history but disappear from tick boxes. Names must be different from each other." */
export const GS_PEOPLE_NOTE = "";
/** Was: "Items at these stores get this tax, tip and category unless you set one on the item. Tip is worked out on the pre-tax amount. Presets from the Sheet hold tax and tip together; tap one to split it into tax and tip. The merchant type is the code card networks use for rewards (dining, groceries, gas); scanned receipts fill it in." */
export const GS_STORES_NOTE = "";

// ---- Invite
/** Was: "First and last, so two people with the same first name don't get mixed up." */
export const INVITE_NAME_NOTE = "";

// ---- Money
/** Was: "Leave rates blank to have the AI on your computer suggest them (in the background); you can change them any time. A card we know fills these in when you type its name." */
export const MONEY_RATES_NOTE = "";
/** Was: "Cards you add here show up in every group's card picker." */
export const MONEY_CARDS_NOTE = "";
/** Was: "Add Amex Membership Rewards, Rakuten and others with their posted and pending balances (from their apps). Cards can point at a program, and Rakuten can pay out into Membership Rewards. Linking them automatically isn't possible yet; neither offers a way to connect." */
export const MONEY_PROGRAMS_NOTE = "";
/** Was: "Add your checking, savings or brokerage accounts with a balance you type in. Connecting a bank to update them automatically can come later." */
export const MONEY_ACCOUNTS_NOTE = "";
/** Was: "The AI on your computer reads the balances, dates and notes above and suggests moves. Suggestions only: nothing moves money, and it isn't financial advice." */
export const MONEY_ADVICE_NOTE = "";
/** Was: "Automatic balances (bank sync) can plug in here later; for now balances are what you enter." */
export const MONEY_FOOT_NOTE = "";

// ---- Roadmap
/** Was: "Not planned yet; picked from when there's time." */
export const ROADMAP_LATER_NOTE = "";
/** Was: "Only you see this." */
export const ROADMAP_PRIVATE_NOTE = "";

// ---- Setup
/** Was: "Your first and last name, so you aren't mixed up with someone who shares your first name." */
export const SETUP_NAME_NOTE = "";

// ---- SignIn
/** Was: "Shared purchases, split to the cent." */
export const SIGNIN_TAGLINE = "";
/** Was: "Create the treasurer's account. It uses a passkey (Face ID, Touch ID or your phone), so there's no password." */
export const SIGNIN_SETUP_NOTE = "";
/** Was: "Pick every group you're part of. You'll see everything in it, even before anything is bought." */
export const SIGNIN_GROUPS_NOTE = "";
/** Was: "Face ID, Touch ID or your phone's screen lock. There's no password to remember." */
export const SIGNIN_PASSKEY_NOTE = "";

// ---- Welcome
/** Was: "A group is one shared ledger, like your Costco runs or a trip. You can add more later and import your Google Sheet in Settings." */
export const WELCOME_NOTE = "";

// ---- You
/** Was: "See the app exactly the way your friends' accounts do: only your own items in each group, item totals without tax or tip details, and no editing. Uses whichever person is set as you in each group's settings." */
export const YOU_MEMBER_VIEW_NOTE = "";
/** Was: "To join another group, open the invite link you were sent while signed in, or pick it on the Overview." */
export const YOU_JOIN_NOTE = "";
/** Was: "How you show up on items and charts, in every group you're in." */
export const YOU_COLOR_NOTE = "";
/** Was: "Updates arrive on their own. Anything you haven't synced stays on this device through an update, so nothing is lost." */
export const YOU_UPDATES_NOTE = "";
/** Was: "A full copy for your records: every group with its complete history and your cards (JSON, which Import can bring back), or every item as a spreadsheet (CSV)." */
export const YOU_BACKUP_NOTE = "";
/** Was: "Use the ledger without opening the site. Make a personal token, then paste it where you want to use it. Each token can be revoked here, which cuts that device or chat off. A token that can't make changes is enough to check balances or for backups." */
export const YOU_TOKEN_NOTE = "";

// ---- Mine
/** Was: "Ask to buy a package and it'll show up here." */
export const MINE_PACKAGE_NOTE = "";

// ---- GroupSettings (more)
/** Was: "Colors are picked for you, spreading across the palette. Change any of them below." */
export const GS_COLORS_NOTE = "";
/** Was: "Your own cards come from the Money page and show up in every group's card picker; add them there. Use this list for Zelle, cash, or friends' cards." */
export const GS_CARDS_NOTE = "";
/** Was: "Bring items, people and payments into the group. Every row becomes a normal change with its own history and undo, and importing the same file twice adds nothing." */
export const GS_IMPORT_NOTE = "";

// ---- Receipts page (Oct 26: numbers only)
/** Was: "Type the total printed on each receipt and the app checks that the rows add up to it." */
export const RECEIPTS_LEDE = "";
/** Was: "Every item number you've bought, with its latest price. Add other names it goes by (search finds them), an icon, and whether it's usually taxed." */
export const CATALOG_NOTE = "";
/** Was: "The model on your computer (LM Studio, set up on the Ask page) suggests what each product is, other names for it, an icon, and whether it's usually taxed in California. It works from what it already knows (no web search), never changes notes you wrote, and runs only while this page is open on your computer." */
export const CATALOG_AI_NOTE = "";
/** Was: "You can change who's in on each item afterwards." (under "Split between" when adding a scan) */
export const SCAN_SPLIT_NOTE = "";

// ---- Ledger
/** Was: "Nobody ticked" on a phone row (the row already says "Nobody is ticked yet."). */
export const LEDGER_NOBODY_TICKED = "";

// ---- Overview of a group ({amount} = the spending)
/** Was: "{amount} spent across {trips} trips, not counting carried-over balances." */
export const GROUP_OVERVIEW_LEDE = "{amount} spent";
/** Was: "Your part comes to {amount}, not counting carried-over balances." (a member) */
export const MEMBER_OVERVIEW_LEDE = "{amount} your part";
